import { useRef, useState } from "react";
import { LinkAnchor } from "../../elements/link/link";
import PlayButtonIndicator from "../../elements/play-button/play-button-indicator";
import VideoModal from "../../elements/video-modal/video-modal";
import styles from "./teaser-block.module.less";
import { getImageLoading, isMobileDevice, isOrHandleAsExternalUrl } from "../../../helpers";
import type { TIndexableBlock, TImage, TLink } from "../../../types";
import Heading from "../../elements/heading/heading";
import { MotionProps, m as motion } from "motion/react";
import { TAnimationConditions, useAnimateFadeIn, useAnimateFadeInToTop } from "../../../animation/animation";
import { useTranslation } from "react-i18next";

export type TVideoObject = {
  posterUrl: string;
  videoUrl: string;
};

export type TYoutubeVideoObject = {
  posterUrl: string;
  youtubeVideoId: string;
};

export type TDesktopMediaPosition = "left" | "right";
export type TMediaShape = "standard" | "round";

type TMobileVideoPlayerProps = {
  videoUrl: string;
  posterUrl: string;
  mediaShape: TMediaShape;
  desktopMediaPosition: TDesktopMediaPosition;
  innerVideoWrapperAnimation?: MotionProps;
  playButtonWrapperAnimation?: MotionProps;
};

const MobileVideoPlayer = ({
  videoUrl,
  posterUrl,
  desktopMediaPosition,
  mediaShape,
  innerVideoWrapperAnimation,
  playButtonWrapperAnimation,
}: TMobileVideoPlayerProps) => {
  const videoElement = useRef<HTMLVideoElement>(null);

  const { t } = useTranslation();

  const playVideo = () => {
    if (videoElement.current) {
      videoElement?.current?.play();
    }
  };
  return (
    <div className={[styles.videoWrapper, styles[desktopMediaPosition]].join(" ")}>
      <motion.button
        {...innerVideoWrapperAnimation}
        className={[styles.innerVideoWrapper, styles[mediaShape]].join(" ")}
        onClick={playVideo}
        aria-label={t("PLAY_VIDEO")}
      >
        <video className={styles.video} ref={videoElement} src={videoUrl} preload="auto" poster={posterUrl} />
        <motion.div {...playButtonWrapperAnimation} className={styles.playButtonWrapper}>
          <PlayButtonIndicator />
        </motion.div>
      </motion.button>
    </div>
  );
};

type TVideoPosterAndModalProps = {
  posterUrl?: string;
  videoUrl?: string;
  youtubeVideoId?: string;
  isVideoModalOpen: boolean;
  desktopMediaPosition: TDesktopMediaPosition;
  mediaShape: TMediaShape;
  innerVideoWrapperAnimation?: MotionProps;
  playButtonAnimation?: MotionProps;
  onClickPlayButton: () => void;
  onCloseModalButton: () => void;
};

const VideoPosterAndModal = ({
  posterUrl,
  videoUrl,
  youtubeVideoId,
  isVideoModalOpen,
  desktopMediaPosition,
  mediaShape,
  innerVideoWrapperAnimation,
  playButtonAnimation,
  onClickPlayButton,
  onCloseModalButton,
}: TVideoPosterAndModalProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  return (
    <div className={[styles.videoWrapper, styles[desktopMediaPosition]].join(" ")}>
      <motion.button
        {...innerVideoWrapperAnimation}
        onClick={onClickPlayButton}
        className={[styles.innerVideoWrapper, styles[mediaShape]].join(" ")}
        aria-label={t("PLAY_VIDEO")}
      >
        <div className={styles.playButtonWrapper}>
          <PlayButtonIndicator animation={playButtonAnimation} />
        </div>
        <div className={styles.videoPosterWrapper}>
          <img className={styles.videoPoster} src={posterUrl} alt="" />
        </div>
      </motion.button>
      <VideoModal
        portalContainer={ref.current ?? undefined}
        close={{ function: onCloseModalButton }}
        onEscapeKeyDown={onCloseModalButton}
        isOpen={isVideoModalOpen}
        videoUrl={videoUrl}
        youtubeVideoId={youtubeVideoId}
      />
      <div ref={ref} />
    </div>
  );
};

export type TTeaserBlockProps = {
  date?: string;
  eyebrow?: string;
  heading: string;
  content?: string;
  text?: string;
  link?: TLink;
  image?: TImage;
  youtubeVideoObject?: TYoutubeVideoObject;
  videoObject?: TVideoObject;
  mediaShape?: TMediaShape;
  desktopMediaPosition?: TDesktopMediaPosition;
  backgroundVideoUrl?: string;
} & TIndexableBlock;

export const TeaserBlock = ({
  date,
  eyebrow,
  heading,
  content,
  text,
  link,
  image,
  videoObject,
  youtubeVideoObject,
  backgroundVideoUrl,
  mediaShape = "standard",
  desktopMediaPosition = "right",
  index,
}: TTeaserBlockProps) => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const conditions: TAnimationConditions = { blockIndex: index };
  const fadeInAnimation = useAnimateFadeIn({ whileInView: true, easing: "easeInOut" }, conditions);
  const fadeInAnimationWithDelay = useAnimateFadeIn({ whileInView: true, delay: 0.3, duration: 0.3 }, conditions);
  const fadeInToTopAnimation = useAnimateFadeInToTop({ whileInView: true, delay: 0.3, easing: "easeOut" }, conditions);

  const isInVideoMode = () => {
    if (videoObject || youtubeVideoObject) {
      if (!image?.url && !backgroundVideoUrl) {
        return true;
      }
    }
    return false;
  };

  const shouldDisplayNativeVideo = videoObject && !image?.url && !backgroundVideoUrl;
  const shouldDisplayYoutubeVideo = youtubeVideoObject && !videoObject && !image?.url && !backgroundVideoUrl;

  return (
    <section className={styles.wrapper}>
      <div
        className={[styles.contentWrapper, isInVideoMode() ? styles.videoMode : "", "max-width padded-sides"].join(" ")}
      >
        <div className={styles.textWrapper}>
          <motion.div {...fadeInAnimation}>
            {(date || eyebrow) && (
              <div className={styles.tagDateWrapper}>
                {date && <p className={[styles.date, "eyebrow"].join(" ")}>{date}</p>}
                {eyebrow && <p className={[styles.eyebrow, "eyebrow"].join(" ")}>{eyebrow}</p>}
              </div>
            )}
            {heading && (
              <Heading
                index={index}
                semanticType={"h2"}
                visualType={"h3"}
                className={[styles.heading, "heading-max-width"].join(" ")}
              >
                {heading}
              </Heading>
            )}
            {content && (
              <div
                className={[styles.content, "html-content"].join(" ")}
                dangerouslySetInnerHTML={{ __html: content }}
              />
            )}
            {text && <p className={["body-base", styles.text].join(" ")}>{text}</p>}
          </motion.div>
          {link && (
            <motion.div {...fadeInToTopAnimation} className={styles.buttonWrapper}>
              <LinkAnchor.Primary
                url={link?.url}
                label={link?.title}
                iconRight={"ui-icon-arrow-right"}
                openInNewTab={isOrHandleAsExternalUrl(link.url, link.target)}
                onPreNavigate={link.onPreNavigate}
              />
            </motion.div>
          )}
        </div>
        {backgroundVideoUrl && (
          <motion.div
            {...fadeInAnimation}
            className={[styles.backgroundMediaWrapper, styles[desktopMediaPosition]].join(" ")}
          >
            <div className={[styles.innerBackgroundMediaWrapper, styles[mediaShape]].join(" ")}>
              <video className={styles.backgroundVideo} src={backgroundVideoUrl} playsInline autoPlay muted loop />
            </div>
          </motion.div>
        )}
        {!backgroundVideoUrl && image?.url && (
          <motion.div
            {...fadeInAnimation}
            className={[styles.backgroundMediaWrapper, styles[desktopMediaPosition]].join(" ")}
          >
            <div className={[styles.innerBackgroundMediaWrapper, styles[mediaShape]].join(" ")}>
              <img className={styles.image} src={image.url} alt={image.alt} loading={getImageLoading(index)} />
            </div>
          </motion.div>
        )}
        {shouldDisplayNativeVideo && (
          <>
            {isMobileDevice() ? (
              <MobileVideoPlayer
                desktopMediaPosition={desktopMediaPosition}
                mediaShape={mediaShape}
                posterUrl={videoObject.posterUrl}
                videoUrl={videoObject.videoUrl}
                innerVideoWrapperAnimation={fadeInAnimation}
                playButtonWrapperAnimation={fadeInAnimationWithDelay}
              />
            ) : (
              <VideoPosterAndModal
                desktopMediaPosition={desktopMediaPosition}
                mediaShape={mediaShape}
                posterUrl={videoObject.posterUrl}
                videoUrl={videoObject.videoUrl}
                isVideoModalOpen={isVideoModalOpen}
                innerVideoWrapperAnimation={fadeInAnimation}
                playButtonAnimation={fadeInAnimationWithDelay}
                onCloseModalButton={() => setIsVideoModalOpen(false)}
                onClickPlayButton={() => setIsVideoModalOpen(true)}
              />
            )}
          </>
        )}
        {shouldDisplayYoutubeVideo && (
          <VideoPosterAndModal
            desktopMediaPosition={desktopMediaPosition}
            mediaShape={mediaShape}
            posterUrl={youtubeVideoObject?.posterUrl}
            youtubeVideoId={youtubeVideoObject?.youtubeVideoId}
            isVideoModalOpen={isVideoModalOpen}
            innerVideoWrapperAnimation={fadeInAnimation}
            playButtonAnimation={fadeInAnimationWithDelay}
            onCloseModalButton={() => setIsVideoModalOpen(false)}
            onClickPlayButton={() => setIsVideoModalOpen(true)}
          />
        )}
      </div>
    </section>
  );
};

export default TeaserBlock;
